import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScrollText, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/** Per-integration "log API calls & responses" switch — shown at the top of
 * each integration's settings card. When ON, every outgoing call for this
 * provider is written to the Admin -> Error Log (full request/response,
 * secrets redacted) so the admin can inspect exactly what was sent/received. */
export function IntegrationApiLogToggle({ provider }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/integrations/log-settings`, { withCredentials: true })
      .then(({ data }) => setEnabled(!!data?.[provider]))
      .catch((e) => console.error("Failed to load log settings:", e))
      .finally(() => setLoading(false));
  }, [provider]);

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/integrations/log-settings/${provider}`,
        { enabled: checked },
        { withCredentials: true }
      );
      setEnabled(checked);
      toast.success(checked ? "API call logging turned on" : "API call logging turned off");
    } catch (e) {
      console.error("Failed to update log setting:", e);
      toast.error("Failed to update logging setting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between border rounded-md p-3 bg-muted/30"
      data-testid={`integration-log-toggle-${provider}`}
    >
      <div className="flex items-center gap-2">
        <ScrollText className="h-4 w-4 text-muted-foreground shrink-0" />
        <div>
          <Label htmlFor={`log-toggle-${provider}`} className="text-sm font-medium cursor-pointer">
            Log API calls &amp; responses
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            When on, every request/response for this integration is recorded in the Log.
          </p>
        </div>
      </div>
      {loading || saving ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <Switch
          id={`log-toggle-${provider}`}
          checked={enabled}
          onCheckedChange={handleToggle}
          data-testid={`integration-log-switch-${provider}`}
        />
      )}
    </div>
  );
}

export default IntegrationApiLogToggle;
